import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ICheckOption, ILandownerIntakeModel, IPropertyChange } from '@app/interfaces';
import { JsonPatchOperation } from '@oa/shared-core';
import { KeyValuePair } from '@app/interfaces/key-value-pair';
import BookingContactData from '@app/models/booking-contact-data';
import ContactSearchRequest from '@app/models/contact-search-request';
import CountyStateFIPS from '@app/models/county-state-fips';
import ListingContactData from '@app/models/listing-contact-data';

import { GeoJSONObject } from '@turf/turf';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { Contact, ListingContact, ListingContactType, XrefListingContact } from '../models';
import { HttpHelperService } from '@oa/shared-core';
import ListingContactViewModel2 from '@app/models/listing-contact-view-model2';
import { tap } from 'rxjs/operators';
import { ListingPrototype } from '@oa/shared-core';
import ListingContactHierarchicalData from '@app/models-internal/listing-contact-hierarchical-data';
import { DateService, LeaseRateCardViewModel, ValueService } from '@oa/shared-core';
import PagedResults from '@app/models/paged-results';
import ValidationResultModel from '@app/models/validation-result-model';
import ListingContactViewModel from '@app/models/listing-contact-view-model';

@Injectable()
export class ListingContactService {
  private readonly _componentName = 'ListingContactService';

  public activeListingPrototype = new BehaviorSubject<ListingPrototype>(null);

  public contactPropertyChange = new BehaviorSubject<IPropertyChange>(null);

  public activeContact = new BehaviorSubject<ListingContactViewModel2>(null);

  /**
   * Active Lead Status Values - These values are a subset of the rows in the LeadStatus table
   */
  private _activeLeadStatusValues: KeyValuePair<string, number>[] = [
    { key: 'Needs Contact', value: 10 },
    { key: 'Needs Follow-up', value: 15 },
    { key: 'Follow-up 1', value: 72 },
    { key: 'Follow-up 2', value: 73 },
    { key: 'Follow-up 3', value: 74 },
    { key: 'Last Call/Follow-up', value: 75 },
    { key: 'Long Term Follow-up', value: 76 },
    { key: 'Agreement Sent/Pending', value: 70 },
    { key: 'Agreement Complete', value: 71 },
    { key: 'Ready for Assessment', value: 116 },
    { key: 'Ready For Site Visit', value: 16 },
    { key: 'Site Visit Scheduled', value: 17 },
    { key: 'Assessment Complete', value: 120 },
    { key: 'Ready for Approval', value: 118 },
    { key: 'Listing Approval', value: 18 },        
    { key: 'Not Viable', value: 50 },
    { key: 'Not Interested', value: 60 },
  ];

  /**
   * Activity Options for Lead Contacts
   */
  private _leadContactActivities: ICheckOption[] = [
    { name: 'Hunting $$$', checked: false, id:'hunting-checkbox-option' },
    { name: 'Fishing $$', checked: false, id:'fishing-checkbox-option' },
    { name: 'Camping $', checked: false, id:'camping-checkbox-option' },
  ];

  /**
   * Landowner Onboard Phone Numbers by state (including a Fallback number for state: ANY)
   *  */
  private _landownerOnboardPhoneNumber = [
    {
      state: 'VA',
      phone: '(804) 381-4222',
    },
    {
      state: 'NC',
      phone: '919-355-5086',
    },
    {
      state: 'SC',
      phone: '803-999-3683',
    },
    {
      state: 'PA',
      phone: '484-773-1777',
    },
    {
      state: 'TX',
      phone: '512-434-0133',
    },
    {
      state: 'ANY',
      phone: '(804) 381-4222',
    },
  ];

  constructor(
    private http: HttpClient,
    private httpHelper: HttpHelperService,
    private dateHelper: DateService,
    private valueHelper: ValueService
  ) {}

  /**
   * Sets the active contact.  This method will broadcast the change via the activeContact BehaviorSubject
   * @param result
   */
  public setActiveContact(contact: ListingContactViewModel2) {
    // console.log(`${this._componentName}.setActiveContact contact=`, contact);
    this.setLandOwnerLeadIntakeData({
      contact: contact as any as ListingContact,
      timestamp: this.dateHelper.nowUTC,
    });

    this.activeContact.next(contact);
  }

  /**
   * Gets all of the listing contact view models for a contact id
   */
  public getListingContactsByContactIdAsync(contactId: number) {
    return this.http.get<ListingContactViewModel[]>(`ListingContact/${contactId}`);
  }

  /**
   * Gets the most recent listing contact (api/ListingContact/most-recent)
   */
  public getMostRecentContact() {
    return this.http.get<ListingContact>('ListingContact/most-recent');
  }

  /**
   * Gets the booking related contacts from the API for a single booking id
   * @param bookingId
   */
  public getBookingContacts(bookingId: string) {
    return this.http.get<BookingContactData[]>(`contact/booking/${bookingId}`);
  }

  /**
   * Gets the primary contact for a listing
   */
  public getPrimaryContact(listingId:number){
    return this.http.get<ListingContact>(`api/listing/${listingId}/contact/primary`);
  }


  

  /**
   * Gets the CountyStateFIPS from the ListingContact Data Json String
   * @param c
   */
  public getCountyState(c: ListingContact): CountyStateFIPS {
    var o = this.getContactData(c);

    if (o && o['countyStateLocation']) {
      return o['countyStateLocation'] as CountyStateFIPS;
    }

    return null;
  }

  /**
   * Gets lease rate estimate for a contact
   * @param contactId
   * @returns
   */
  public getLeaseRate(contactId: number) {
    return this.http.get<LeaseRateCardViewModel>(`ListingContact/${contactId}/lease-rate-est`);
  }

  /**
   * Creates a new array of lead contact activities (e.g. Hunting, Fishing, Camping)
   */
  public createLeadContactActivities() {
    return Object.assign([], this._leadContactActivities);
  }

  /**
   * Gets the ListingContactData.Data Property string as a JSON object.
   */
  public getContactData(c: ListingContact | ListingContactData | ListingContactViewModel2) {
    if (c && c.data) {
      try {
        if (typeof c.data === 'string') {
          return JSON.parse(c.data) as ListingContactHierarchicalData;
        } else if (typeof c.data === 'object') {
          return c.data;
        } else {
          throw new Error("Invalid 'data' value");
        }
      } catch (error) {
        console.error(error);
        console.log(`${this._componentName}.getContactData c=`, c);
        
      }
    }

    return null;
  }

  /**
   * Gets a listing prototype for a listing contact
   * @param contactId
   */
  public getListingPrototype(contactId: number): Observable<ListingPrototype> {
    // api/listing/0/admin

    return this.http.get<ListingPrototype>(`ListingContact/${contactId}/listing/prototype`);
  }

  /**
   * Gets a listing contact view model by its Secure ID (UUID)
   * @param contactUUID
   */
  public getBySecureId(contactUUID: string) {
    return this.http.get<ListingContactViewModel2>(`ListingContact/${contactUUID}`);
  }

  /**
   * Gets a listing contact view model by LeadPropertyId
   * @param propertyId
   * @returns
   */
  public getByPropertyId(propertyId: string) {
    return this.http.get<ListingContactViewModel2>(`ListingContact/${propertyId};idType=LeadPropertyId`);
  }

  /**
   * Gets the selected property parcels from a ListingContact data property
   * @param c
   */
  public getSelectedPropertyParcels(c: ListingContactData | ListingContact): any[] {
    var o = this.getContactData(c);
    if (o && o['selectedPropertyParcels']) {
      let items = o['selectedPropertyParcels'] as any[];

      items = items.filter((x) => x != null && x != undefined);

      items.forEach((item) => {
        item.selected = true;
      });

      return items;
    }
  }

  /**
   * Gets a geojson feature collection for a contact's selected parcels
   * @param secureAlternateId
   */
  public getSelectedParcelGeojsonFeatureCollection(secureAlternateId: string): Observable<GeoJSONObject> {
    return this.http.get<GeoJSONObject>(`ListingContact/${secureAlternateId}/selected-parcels`);
  }

  /**
   * Gets a point feature collection for a contact's selected parcels
   * @param secureAlternateId
   */
  public getSelectedPropertyParcelCenterPoints(secureAlternateId: string) {
    return this.http.get<GeoJSONObject>(`ListingContact/${secureAlternateId}/selected-parcels/center-points`);
  }

  /**
   * Gets the  landowner-intake-data data stored in localstorage
   */
  public getLandOwnerLeadIntakeData(): ILandownerIntakeModel {
    const d = localStorage.getItem('landowner-intake-data');

    if (d) {
      let returnItem = JSON.parse(d) as ILandownerIntakeModel;

      return returnItem;
    }

    console.warn(`${this._componentName}.getLandOwnerLeadIntakeData() -  landowner-intake-data is null/empty.`);
    return null;
  }

  /**
   * Sets the landowner-intake-data value
   */
  public setLandOwnerLeadIntakeData(data: ILandownerIntakeModel) {
    if (data) {
      let a = data as any;
      a.timestamp = new Date();
      localStorage.setItem('landowner-intake-data', JSON.stringify(a));
    } else {
      localStorage.setItem('landowner-intake-data', null);
    }
  }

  /**
   * Clears the local storage "landowner-intake-data" item
   */
  public clearLandOwnerLeadIntakeData() {
    this.setLandOwnerLeadIntakeData(null);
  }

  /**
   * Updates a portion of a model (using JSON Patch Notation)
   * @param id    The id of the listing
   * @param path  The path of the poperty value being modified. (e.g. "./endDate" for TripReservationModel.endDate)
   * @param value The new value. Undefined or null will be treated as "remove" patch operations
   */
  patch<K extends keyof ListingContact>(id: number, path: K, value: ListingContact[K]): Observable<{}> {
    return this.patchJson(id, [
      {
        path,
        value,
        op: 'replace',
      },
    ]);
  }

  updateXrefListingContact(contact: ListingContact, listingId: number) {
    const updateModel: XrefListingContact = <XrefListingContact>{};
    updateModel.contactId = contact.id;
    updateModel.isListingManager = contact.isListingManager;
    updateModel.isNamedInsured = contact.isNamedInsured;
    updateModel.contactRole = contact.contactRole;
    updateModel.notes = contact.notes;
    updateModel.listingId = listingId;
    return this.http.post('listing/' + listingId + '/contact/' + contact.id + '/', updateModel);
  }

  patchJson<K extends keyof ListingContact>(id: number, patch: ListingContactPatch<K>[]): Observable<ListingContact> {
    return this.http.patch<ListingContact>(
      `ListingContact/${id}`,
      patch.map((p) => ({ ...p, path: `/${p.path}` }))
    );
  }
  getContacts(listingId: number): Observable<ListingContact[]> {
    return this.http.get<ListingContact[]>('listing/' + listingId + '/contact/');
  }
  getContactRoles(): Observable<ListingContactType[]> {
    return this.http.get<ListingContactType[]>('ListingContact/ListingContactType');
  }

  /**
   * Listing Contact Search
   * @param searchRequest
   */
  public search(searchRequest: ContactSearchRequest): Observable<PagedResults> {
    let searchParameters = {};

    Object.keys(searchRequest).forEach((key) => {
      searchParameters[key] = searchRequest[key] + '';
    });

    const httpOptions = {
      headers: { 'Content-Type': 'application/json' },
      params: { ...searchParameters },
    };

    return this.http.get<PagedResults>('ListingContact/search', httpOptions);
  }

  searchbyCell(cell: string): Observable<ListingContact[]> {
    return this.http.get<ListingContact[]>('ListingContact/searchByCell?cell=' + cell);
  }
  contactUpdate(id: number, contact: ListingContact) {
    console.log(contact);
    return this.http.patch<ListingContact>('ListingContact/' + id, contact);
  }
  // unlinks contact from listing
  contactRemove(contact: ListingContact, listingid: number) {
    console.log('Deleting' + contact.id);
    return this.http.delete('listing/' + listingid + '/contact/' + contact.id);
  }
  // Sets contact role relative to listing
  setRole(contact: ListingContact, listingId: number, role: number) {
    const c = contact;
    c.contactRole = role;
    return this.updateXrefListingContact(c, listingId);
  }
  setListingManager(contact: ListingContact, listingId: number, isListingManager: boolean) {
    const c = contact;
    c.isListingManager = isListingManager;
    return this.updateXrefListingContact(c, listingId);
  }

  setNamedInsured(contact: ListingContact, listingId: number, isNamedInsured: boolean) {
    const c = contact;
    c.isNamedInsured = isNamedInsured;
    return this.updateXrefListingContact(c, listingId);
  }

  // assigns and optionally adds contact to listing
  listingContactAdd(contact: ListingContact, listingId?: number) {
    console.log('Submitting', contact);
    contact.createdOn = new Date();
    contact.updatedOn = new Date();

    let contactData = this.getContactData(contact);

    if (!contactData) {
      contactData = {
        activities: [],
        countyStateLocation: undefined,
        events: [],
        searchPropertyAddress: undefined,
        searchPropertyAddressLocation: undefined,
        selectedPropertyParcels: [],
        webhookPayload: undefined,
        leaseRate: undefined,
      };
    }
    if (contactData) {
      if (!contactData['activities'] || contactData['activities'].length === 0) {
        contactData['activities'] = [
          { name: 'Hunting $$$', checked: false },
          { name: 'Fishing $$', checked: false },
          { name: 'Camping $', checked: false },
        ];
      }
    }

    contact.data = JSON.stringify(contactData);

    if (listingId === undefined || listingId === null) {
      return this.http.post<ListingContact>('ListingContact/', contact);
    } else {
      return this.http.post<ListingContact>('listing/' + listingId + '/contact/', contact);
    }
  }
  // assigns existing contact to listing
  contactAssign(contact: ListingContact, listingId: number) {
    console.log('assignment test', contact, listingId);
    const contactRelation = {
      contactId: contact.id,
      listingId: listingId,
      isListingManager: contact.isListingManager,
      contactRole: contact.contactRole,
      notes: contact.notes,
    };
    return this.http.post<XrefListingContact>(`listing/${listingId}/contact/${contact.id}/assign`, contactRelation);
  }
  // updates relation specific data on a contact
  updateRelation(relation: XrefListingContact) {
    console.log(relation);
    return this.http.post<ListingContact>('listing/' + relation.listingId + '/contact/' + relation.contactId, relation);
  }

  // -----------------------------------------------------------------------------
  // These methods operate on the basic Contact type, not the ListingContact type
  // -----------------------------------------------------------------------------
  contactAdd(contact: Contact) {
    console.log('Submitting', contact);
    contact.createdOn = new Date();
    contact.updatedOn = new Date();

    return this.http.post<Contact>('Contact/', contact);
  }

  /**
   * Active Lead Status values for Listing Contact Leads
   */
  public get activeLeadStatusValues(): KeyValuePair<string, number>[] {
    return this._activeLeadStatusValues;
  }

  /**
   * Updates a listing contact
   * @param contact
   */
  public updateContact(contact: ListingContact) {
    const url = 'ListingContact';

    return this.http.put<ListingContact>(url, contact).pipe(
      tap((result) => {
        // if (result && result.id) this.onContactPropertyChange(result.id);
      })
    );
  }

  /**
   * Updates the contact data field (the JSON data field for ListingContact)
   * @param contactId
   * @param contactData
   */
  public updateContactData(contactId: number, contactData: object) {
    const url = `ListingContact/${contactId}/data`;
    return this.http.put<ListingContact>(url, contactData).pipe(
      tap((result) => {
        if (result && result.id)
          this.onContactPropertyChange(result.id, [
            { op: 'replace', path: 'data', value: JSON.stringify(contactData) } as JsonPatchOperation,
          ]);
      })
    );
  }

  /**
   * Patches a single value for a ListingContact
   * @param id
   * @param path
   * @param value
   */
  public patchValue(id: number, path: string, value: any) {
    let patchOperation = { path, value, op: 'replace' } as JsonPatchOperation;

    // TODO: need to check the patch result response for a success. for now, updated event gets raised regardless
    return this.httpHelper
      .patch(`ListingContact/${id}`, [patchOperation])
      .pipe(tap((result) => this.onContactPropertyChange(id, [patchOperation])));
  }

  /**
   * Emits the updated listing contact proeprty c
   */
  private onContactPropertyChange(contactId: number, patchDocument: JsonPatchOperation[]) {
    console.log(`${this._componentName}.onContactPropertyChange contactId=`, contactId);

    if (!patchDocument) {
      return;
    }

    patchDocument.forEach((x) => {
      var change = {
        name: x.path,
        value: x.value,
        timestamp: this.dateHelper.nowUTC,
        data: {
          id: contactId,
          type: 'ListingContact',
        },
      } as IPropertyChange;

      this.contactPropertyChange.next(change);
    });
  }

  /**
   * Calculate a lease rate
   */
  public calculateLeaseRate(leaseRate: LeaseRateCardViewModel) {
    let x: LeaseRateCardViewModel = { ...leaseRate };

    x.totalMemberAmount = leaseRate.maxMembers * leaseRate.memberAmount;
    x.totalLandAmount = leaseRate.maxAcres * leaseRate.amountPerAcre;

    x.totalOAAmount = x.oaLeaseRatio * x.totalLandAmount + x.totalMemberAmount;
    x.totalLOAmount = (1 - x.oaLeaseRatio) * x.totalLandAmount;

    x.totalLeaseAmount = x.totalOAAmount + x.totalLOAmount;
    x.perPersonAmount = x.totalLeaseAmount / x.maxMembers;
    x.totalPerAcre = x.totalLeaseAmount / x.maxAcres;

    x.oaTakeRate = x.totalOAAmount / x.totalLOAmount;

    return x;
  }

  /**
   * Patches multiple values for a ListingContact in a single patch operation
   * @param id
   * @param patchDocument
   */
  public patchValues(id: number, patchDocument: JsonPatchOperation[]) {
    return this.httpHelper
      .patch(`ListingContact/${id}`, patchDocument)
      .pipe(tap((result) => this.onContactPropertyChange(id, patchDocument)));
  }

  public patchValuesBySecureId(contactId: number, secureId: string, patchDocument: JsonPatchOperation[]) {

    return this.httpHelper
      .patch(`ListingContact/${secureId}`, patchDocument)
      .pipe(tap(() => this.onContactPropertyChange(contactId, patchDocument)));
  }

  /**
   * Deletes a listing contact
   * @param contactId
   */
  public deleteContact(contactId: number) {
    return this.http.delete(`ListingContact/${contactId}`);
  }

  /**
   * Adds a selected parcel to the Listing contact's Data.selectedPropertyParcels array
   * @param contactId
   * @param parcel
   */
  public addParcel(contactId: number, parcel: any) {
    const url = `ListingContact/${contactId}/data/parcel`;

    return this.http.post<ListingContact>(url, parcel).pipe(tap((result) => this.onContactPropertyChange(contactId, null)));
  }

  /**
   * Gets a contact by contact id. If the contact is associated with more than 1 Listing, only the first is returned.
   * ListingContactViewModel2 is returned
   * @param contactId
   */
  public getListingContact(contactId: number) {
    const url = `Contact/${contactId}`;

    return this.http.get<ListingContactViewModel2>(url).pipe(
      map((m) => {
        console.log(`${this._componentName}.getListingContact`);

        if (m && m.siteVisitStartDate) {
          m.siteVisitStartDate = this.dateHelper.makeValidUTCDate(m.siteVisitStartDate);
        }

        if (m && m.siteVisitEndDate) {
          m.siteVisitEndDate = this.dateHelper.makeValidUTCDate(m.siteVisitEndDate);
        }

        return m;
      })
    );
  }

  // End of Contact operations

  /**
   * Validates the direct mail code (which is the property id)
   * @param code
   * @returns
   */
  public validateDirectMailReferenceCode(code: string): Observable<ValidationResultModel> {
    const url = `ListingContact/code/${code}/validate`;

    return this.http.get<ValidationResultModel>(url);
  }

  /**
   * Evaluates a listing contact lead to determine if it is a candidate for a site visit
   * @param c
   */
  public isSiteVisitCandidate(c: ListingContactData | ListingContact | ListingContactViewModel2): boolean {
    if (!c) {
      return false;
    }

    const contactData = this.getContactData(c);

    if (contactData && contactData.activities) {
      // 2021-05-17: only offer site visits for Hunting activity leads
      if (!contactData.activities.find((x) => x.name === 'Hunting $$$').checked) {
        // console.log(`${this._componentName}.isSiteVisitCandidate - ListingContact IS NOT a site visit candidate`);

        return false;
      } else {
        // console.log(`${this._componentName}.isSiteVisitCandidate - ListingContact IS a site visit candidate`);
        return true;
      }
    } else {
      return false;
    }
  }

  public isSiteAssessmentCandidate(c: ListingContactData | ListingContact | ListingContactViewModel2): boolean {
    if (!c) {
      return false;
    }
    return true;
  }


  /**
   *  Creates a Calendly Schedule Url using the introduction-to-outdoor-access resource
   * @param contactFullName
   * @param contactEmailAddress
   * @param contactMobilePhone
   * @returns
   */
  public createCalendlyUrl(contactFullName: string, contactEmailAddress: string, contactMobilePhone: string) {
    return this.createCalendlyScheduleUrl('introduction-to-outdoor-access', contactFullName, contactEmailAddress, contactMobilePhone);
  }

  /**
   * Creates a Calendly Schedule Url
   * @param resource (e.g. introduction-to-outdoor-access or intro-to-outdoor-access-camping )
   * @param contactFullName
   * @param contactEmailAddress
   * @param contactMobilePhone
   */
  public createCalendlyScheduleUrl(resource: string, contactFullName: string, contactEmailAddress: string, contactMobilePhone: string) {
    let cellPhone = contactMobilePhone;

    if (cellPhone && !cellPhone.startsWith('+1')) {
      cellPhone = `+1${cellPhone}`;
    }
    //return `https://calendly.com/outdoor-access/${resource}?name=${contactFullName}&email=${contactEmailAddress}&a1=${cellPhone}`;
    return `https://link.outdooraccess.com/widget/booking/Rf9bX9Lpi0XO1UmgSNzU`;
  }

  /**
   * Creates a Calendly Schedule Resource Base Url
   * @param resource (e.g. introduction-to-outdoor-access or intro-to-outdoor-access-camping )
   */
  public createCalendlyScheduleResourceBaseUrl(resource: string) {
    //return `https://calendly.com/outdoor-access/${resource}`;
    return `https://link.outdooraccess.com/widget/booking/Rf9bX9Lpi0XO1UmgSNzU`;
  }

  /**
   * Gets the  Landowner Onboard Phone Numbers for a state
   */
  public getLandownerOnboardPhoneNumber(state: string) {

    if (!state){
      return this._landownerOnboardPhoneNumber.find((x) => this.valueHelper.equalsIgnoringCase(x.state, 'ANY')).phone;
    }

    let match = this._landownerOnboardPhoneNumber.find((x) => this.valueHelper.equalsIgnoringCase(x.state, state));

    if (!match) {
      match = this._landownerOnboardPhoneNumber.find((x) => this.valueHelper.equalsIgnoringCase(x.state, 'ANY'));
    }

    return match.phone;
  }
}

export type ContactPatch<K extends string & keyof Contact> = { path: K; value: Contact[K]; op: 'replace' };
export type ListingContactPatch<K extends string & keyof ListingContact> = { path: K; value: ListingContact[K]; op: 'replace' };
